'use client'
import lodash from 'lodash'
import SvgSpinnerBlue from '@/components/icons/spinner-blue'
import {
  DEMO_LESSON_COURSE_ID,
  DEMO_LESSON_TOPIC_ID,
} from '@/constants/demo-lessons'
import { firebaseGetLessonsByTopicId } from '@/firebase/lessons-ts'
import { useWindowDimensions } from '@/hooks/getWindowDimensions'
import demoLessonSelector from '@/redux/selectors/demo-lesson'
import {
  setCurrentDemoLesson,
  setDemoLessons,
} from '@/redux/slices/demoLessonReducer'
import {
  ExamBoards,
  IHomePageTemplateDemoLessonBlock,
  ILesson,
} from '@/types/firebase'
import { IGenericError } from '@/types/shared'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useDispatch } from 'react-redux'
import Image from 'next/image'
import lessonPreviewImg from '@/images/lesson-preview.png'
import { getExamBoardIcon } from '@/utils/dashboard-utils'
import DemoLessonComponent from '@/components/ui/common/lessons/demo-lessons/DemoLessonComponent'
import { LessonContextProvider } from '@/components/contexts'

const firstLessonId = 'demo-lesson-open-text'

interface Props {
  customContent: IHomePageTemplateDemoLessonBlock
  showImgPreview?: boolean
}

export const CommonDemoLessonBlock: FC<Props> = ({
  showImgPreview,
  customContent,
}) => {
  const [isLoadingLessons, setIsLoadingLessons] = useState(false)
  const [hoveredElementId, setHoveredElementId] = useState<string>()

  const demoLessonComponentRef = useRef<HTMLDivElement>(null)
  const { width } = useWindowDimensions()

  const dispatch = useDispatch()
  const { currentLesson, lessons } = useSelector(demoLessonSelector)

  const getDemoLessons = useCallback(async () => {
    try {
      setIsLoadingLessons(true)

      let lessons: ILesson[] = await firebaseGetLessonsByTopicId(
        DEMO_LESSON_COURSE_ID,
        DEMO_LESSON_TOPIC_ID,
      )

      // get first Open Text type lesson
      const firstLessonOpenText = lessons.find((l) => l.id === firstLessonId)

      // remove first lesson from array and shuffle it, then add first lesson as first element
      // used to show first lesson as regular Open Text type lesson (without images in question)
      // to provide ability to user interact with gpt at the start of demo lessons session
      lessons = lodash.shuffle(lessons.filter((l) => l.id !== firstLessonId))

      // set first lesson at the start of shuffled array
      lessons = firstLessonOpenText
        ? [firstLessonOpenText, ...lessons]
        : lessons

      dispatch(setDemoLessons(lessons))

      firstLessonOpenText && dispatch(setCurrentDemoLesson(firstLessonOpenText))
    } catch (error: unknown) {
      console.error('error  :>> ', (error as IGenericError)?.message, error)
    } finally {
      setIsLoadingLessons(false)
    }
  }, [dispatch])

  useEffect(() => {
    if (showImgPreview) {
      return
    }

    getDemoLessons()
  }, [getDemoLessons, showImgPreview])

  const handleScrollToLessonTop = useCallback(() => {
    const isMobile = width <= 768

    if (demoLessonComponentRef.current && typeof window !== 'undefined') {
      // for desktop view when header is stick to the top we add offset as header height
      const yOffset = isMobile ? 0 : 65 // header height in px

      const y =
        demoLessonComponentRef.current.getBoundingClientRect().top +
        window.pageYOffset -
        yOffset

      window.scrollTo({ top: y, behavior: 'smooth' })
    }
  }, [width])

  return (
    <div
      id="demo-lesson-component"
      // overflow:hidden used to prevent scroll X on small devices for clue text with arrow
      className="w-full max-w-7xl mx-auto p-4 pb-7 sm:p-7 lg:p-8 flex flex-col gap-5 justify-center max-[1440px]:overflow-hidden"
      ref={demoLessonComponentRef}
    >
      <div>
        <h2
          id="headingText"
          className="inline-block font-handwritten tracking-[2px]"
          style={{
            fontSize: `${customContent.headingText.styles.fontSize}px`,
            fontWeight: +customContent.headingText.styles.fontWeight,
            lineHeight: `${customContent.headingText.styles.lineHeight}px`,
            fontStyle: customContent.headingText.styles.isItalic
              ? 'italic'
              : 'normal',
            color:
              hoveredElementId === 'headingText'
                ? customContent.headingText.styles.onHoverColor
                : customContent.headingText.styles.color,
          }}
          onMouseEnter={() => setHoveredElementId('headingText')}
          onMouseLeave={() => setHoveredElementId(undefined)}
        >
          {customContent.headingText.text}
        </h2>
      </div>

      {showImgPreview ? (
        <>
          {/* This component is used to show Demo Lesson Preview in admin/home-page-template create/edit form */}
          <div className="w-full flex min-h-[380px] p-6 bg-white rounded-[24px] shadow-v8-card">
            <div className="w-full relative">
              <Image src={lessonPreviewImg.src} fill alt="lesson-preview" />
            </div>
          </div>
        </>
      ) : (
        <div className="w-full flex min-h-[380px] p-4 bg-white rounded-[24px] shadow-v8-card">
          {isLoadingLessons ? (
            <div className="w-full flex flex-col grow items-center justify-center">
              <SvgSpinnerBlue classess={'w-10 h-10'} withyMargin={false} />
            </div>
          ) : (
            <>
              {lessons && !lessons.length ? (
                <div className="w-full flex flex-col grow items-center justify-center text-v8-mut">
                  Demo lesson will be available soon
                </div>
              ) : (
                <>
                  {currentLesson ? (
                    <LessonContextProvider
                      value={{
                        recommendedVideos: [],
                      }}
                    >
                      <DemoLessonComponent
                        lesson={currentLesson}
                        handleScrollToLessonTop={handleScrollToLessonTop}
                        isHomePageView={true}
                      />
                    </LessonContextProvider>
                  ) : (
                    <div className="w-full flex flex-col grow items-center justify-center text-v8-mut">
                      Demo lesson will be available soon
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Exam Board Icons */}
      <div className="w-full flex items-center justify-end">
        <ul className="px-4 flex items-center gap-2">
          {Object.values(ExamBoards).map((examBoard) => {
            const icon = getExamBoardIcon(examBoard)

            return (
              <li
                key={examBoard}
                className="relative max-w-10 min-w-10 max-h-8 min-h-8"
              >
                <Image
                  className=""
                  alt={`examBoard-${examBoard}-icon`}
                  src={icon.src}
                  fill
                />
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
